/**
 * ============================================================================
 *  File        : frontend/src/api/twoFactor.ts
 *  Module      : api
 *  Purpose     : 2FA (TOTP) API — setup / verify / disable（spec §A1）
 *  Author      : EF Engineering
 *  Created     : 2026-06-02
 *  Updated     : 2026-06-02
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端 handler：crates/api-http/src/routes/totp.rs（掛在 v1 protected_router）。
 *  - POST /api/v1/auth/2fa/setup   → { secret, otpauth_uri }（暫存 secret，尚未啟用）
 *  - POST /api/v1/auth/2fa/verify  → { message_key, two_factor_enabled }（驗證 code → 啟用）
 *  - POST /api/v1/auth/2fa/disable → { message_key, two_factor_enabled }（驗證 code → 停用）
 *  皆需 Bearer（public-after-auth）；setup/verify/disable 本身**不**需要 step-up code。
 */

import { API_BASE, mutationHeaders, requestData } from './client'

/** setup 回應：QR 用 otpauth URI + base32 secret（手動輸入 fallback）。 */
export interface TotpSetupResponse {
  secret: string
  otpauth_uri: string
}

/** verify / disable 回應：i18n message key + 目前 2FA 啟用狀態。 */
export interface TotpStatusResponse {
  message_key: string
  two_factor_enabled: boolean
}

/** 產生新 TOTP secret 並暫存（尚未啟用）；回 secret + otpauth URI 供 QR 顯示。 */
export function setupTwoFactor(): Promise<TotpSetupResponse> {
  return requestData<TotpSetupResponse>(`${API_BASE}/auth/2fa/setup`, {
    method: 'POST',
    body: {},
    headers: mutationHeaders(),
  })
}

/** 以 authenticator 產生的 6 位 code 驗證並啟用 2FA。 */
export function verifyTwoFactor(code: string): Promise<TotpStatusResponse> {
  return requestData<TotpStatusResponse>(`${API_BASE}/auth/2fa/verify`, {
    method: 'POST',
    body: { code: code.trim() },
    headers: mutationHeaders(),
  })
}

/** 以一組有效 code 停用 2FA（清空 secret + enabled_at）。 */
export function disableTwoFactor(code: string): Promise<TotpStatusResponse> {
  return requestData<TotpStatusResponse>(`${API_BASE}/auth/2fa/disable`, {
    method: 'POST',
    body: { code: code.trim() },
    headers: mutationHeaders(),
  })
}
